import { useSearchParams } from 'react-router-dom';
import { SegmentedControl, type TabItem } from '../../../components/ui/Tabs';
import { InfoTip } from '../../../components/ui/InfoTip';
import { ErrorState } from '../../../components/ui/States';
import {
  CollapsibleSection,
  DAY_RANGES,
  DEFAULT_MARKETING_RANGE,
  MARKETING_RANGE_OPTIONS,
  RANGE_PARAM,
  UnavailableSources,
  isDayRange,
  isoDaysAgo,
  rangeLabel,
  type DayRange,
} from '../../Marketing/shared';
import { useBoardQuery, usePerformanceQuery, sectionLabel } from './queries';
import { AiVisibility, BoardDetail, BoardKpis, SearchTrend } from './BoardView';
import { DemoFunnel, FunnelWaterfall, PaidSummary, PerformanceDetail, TopContent } from './MarketingView';
import { MqlCard } from './MqlCard';
import { DealSourcesCard } from './DealSourcesCard';
import { MarketingExportMenu } from './MarketingExportButton';
import { useWebAnalytics } from '../../../hooks/useWebAnalytics';
import { paidClickSessions } from './components/format';
import { formatDayMonthYear } from '../../../utils/dateFormat';

/**
 * Marketing → Overview (formerly the "GTM" board). Six panels up front: headline
 * KPIs, demos and the funnel, organic search trend, paid spend, AI visibility and
 * top content. Every other board / performance panel sits under "More detail".
 * Channel contribution lives on GTM → Channels; content opportunities under
 * Content; Tech / SEO health under Visibility.
 */

// The shared Marketing period chips (same set and default on every tab), plus a
// custom range here only, because the board endpoints take a date range.
type RangePreset = DayRange | 'custom';

const RANGE_OPTIONS: readonly TabItem<RangePreset>[] = [...MARKETING_RANGE_OPTIONS, { id: 'custom', label: 'Custom' }];

function todayStr(): string {
  return new Date().toISOString().slice(0, 10);
}

export function OverviewView() {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawRange = searchParams.get(RANGE_PARAM);
  const range: RangePreset = rawRange === 'custom' || isDayRange(rawRange) ? rawRange : DEFAULT_MARKETING_RANGE;

  const customStart = searchParams.get('cstart') || isoDaysAgo(30);
  const customEnd = searchParams.get('cend') || todayStr();

  const startDate = range === 'custom' ? customStart : isoDaysAgo(DAY_RANGES[range]);
  const endDate = range === 'custom' ? customEnd : todayStr();
  const periodLabel = range === 'custom' ? `${formatDayMonthYear(customStart)} to ${formatDayMonthYear(customEnd)}` : rangeLabel(range);

  const setParam = (key: string, value: string) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set(key, value);
        return next;
      },
      { replace: true },
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-1.5">
          <p className="text-body-xs text-text-tertiary">From Search Console, GA4, Peec, Attio, Calendly and the ad platforms</p>
          <InfoTip label="About this overview">
          Inbound performance since the relaunch, from one data spine: Search Console, GA4, Peec, Attio, Calendly and
          the ad platforms. Every panel loads independently: a slow or failing source shows as unavailable instead of
          holding up the page.
          </InfoTip>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {range === 'custom' && (
            <div className="flex items-center gap-1.5">
              <input
                type="date"
                aria-label="Start date"
                value={customStart}
                max={customEnd}
                onChange={(e) => setParam('cstart', e.target.value)}
                className="rounded border border-border-light bg-surface-primary px-3 py-1 text-body-xs text-text-primary"
              />
              <span className="text-body-xs text-text-tertiary">to</span>
              <input
                type="date"
                aria-label="End date"
                value={customEnd}
                min={customStart}
                max={todayStr()}
                onChange={(e) => setParam('cend', e.target.value)}
                className="rounded border border-border-light bg-surface-primary px-3 py-1 text-body-xs text-text-primary"
              />
            </div>
          )}
          <SegmentedControl ariaLabel="Period" options={RANGE_OPTIONS} value={range} onChange={(id) => setParam(RANGE_PARAM, id)} />
          <MarketingExportMenu />
        </div>
      </div>

      <OverviewPanels startDate={startDate} endDate={endDate} periodLabel={periodLabel} />
    </div>
  );
}

function OverviewPanels({ startDate, endDate, periodLabel }: { startDate: string; endDate: string; periodLabel: string }) {
  const board = useBoardQuery(startDate, endDate);
  const perf = usePerformanceQuery(startDate, endDate);
  // OS-tracked sessions per paid source, for the Paid card's clicks → sessions line.
  const web = useWebAnalytics(startDate, endDate);
  const paidSessions =
    web.isLoading
      ? ('loading' as const)
      : web.error || !web.data?.channels
        ? ('error' as const)
        : paidClickSessions(perf.data?.paidEfficiency?.campaigns ?? [], web.data.channels);

  const unavailable = [...(board.data?.unavailable ?? []), ...(perf.data?.unavailable ?? [])];
  // Why each section failed, as the API reports it.
  const reasons = { ...board.data?.unavailableReasons, ...perf.data?.unavailableReasons };

  return (
    <div className="space-y-6">
      {board.error && <ErrorState title="Couldn’t load the overview" error={board.error} onRetry={() => board.refetch()} />}
      {perf.error && (
        <ErrorState title="Couldn’t load performance marketing" error={perf.error} onRetry={() => perf.refetch()} />
      )}
      <UnavailableSources sources={unavailable} label={sectionLabel} reasons={reasons} />

      {/* The tile rows hold their place while loading, so nothing jumps in above the demo tiles. */}
      {board.isLoading ? (
        <TileRowSkeleton count={5} />
      ) : (
        board.data && (
          <BoardKpis
            data={board.data}
            periodLabel={periodLabel}
            requestDemoFormLeads={
              perf.data?.leadFunnel
                ? perf.data.leadFunnel.byForm.filter((f) => f.form.startsWith('Request demo')).reduce((n, f) => n + f.leads, 0)
                : null
            }
          />
        )
      )}

      {perf.isLoading ? (
        // Four demo tiles: no-shows stay hidden until someone marks them in Calendly.
        <TileRowSkeleton count={4} eyebrow />
      ) : (
        perf.data?.demoFunnel && <DemoFunnel data={perf.data} />
      )}
      <MqlCard report={perf.data?.mqlReport} loading={perf.isLoading} />
      {/* The pipeline headline management and the board read: in the main view, full width. */}
      {board.data && <DealSourcesCard data={board.data} />}
      <FunnelWaterfall
        funnel={perf.data?.leadFunnel ?? null}
        loading={perf.isLoading}
        demosBooked={board.data?.scorecards.find((c) => c.key === 'demos_booked')?.value ?? null}
        demoSources={perf.data?.demoBookingSources ?? null}
        pipelineDeals={board.data?.sourcedPipeline ? board.data.sourcedPipeline.reduce((n, r) => n + r.dealCount, 0) : null}
      />

      {board.data && <SearchTrend data={board.data} />}

      {perf.data && (
        <PaidSummary
          data={perf.data.paidEfficiency ?? null}
          funnel={perf.data.leadFunnel ?? null}
          periodLabel={periodLabel}
          paidSessions={paidSessions}
        />
      )}

      {board.data && <AiVisibility data={board.data} />}

      {(perf.data?.linkedinOrganic?.totals.posts ?? 0) > 0 && <TopContent data={perf.data} />}

      {(board.data || perf.data) && (
        <CollapsibleSection
          title="More detail"
          description="Brand vs category, competitors, ICP fit, markets, email, attribution, search movement"
        >
          {board.data && <BoardDetail data={board.data} />}
          {perf.data && <PerformanceDetail data={perf.data} />}
        </CollapsibleSection>
      )}
    </div>
  );
}

/** Placeholder tiles at the height of a StatTile row, so the page keeps its layout while loading. */
function TileRowSkeleton({ count, eyebrow = false }: { count: number; eyebrow?: boolean }) {
  return (
    <div aria-busy="true" aria-label="Loading">
      {eyebrow && <div className="mb-3 h-3 w-48 animate-pulse rounded bg-surface-tertiary" />}
      <div className={`grid grid-cols-2 gap-4 md:grid-cols-3 ${count === 4 ? 'lg:grid-cols-4' : 'lg:grid-cols-5'}`}>
        {Array.from({ length: count }).map((_, i) => (
          <div key={i} className="h-[8.5rem] animate-pulse rounded-2xl border border-border-light bg-surface-primary p-6">
            <div className="h-3 w-24 rounded bg-surface-tertiary" />
            <div className="mt-4 h-8 w-20 rounded bg-surface-tertiary" />
          </div>
        ))}
      </div>
    </div>
  );
}
